import ArrowForwardSharpIcon from '@mui/icons-material/ArrowForwardSharp';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
	Card,
	CardActions,
	CardHeader,
	CardContent,
	Grid,
	Typography,
	IconButton,
	ListItem,
	ListItemText,
	Divider,
	Stack,
	Icon,
	TextField,
	Avatar,
	Box,
} from '@mui/material';

import React, { useEffect } from 'react';

import { useDispatch } from 'react-redux';

import {
	Chart as ChartJS,
	CategoryScale,
	Tooltip,
	Title,
	Legend,
} from 'chart.js';
import * as ChartGeo from 'chartjs-chart-geo';

import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import ButtonComp from 'common/components/Button/ButtonComp';

import {
	calendarIcon,
	rightArrowOutline,
	downchevronIcon,
	Filter,
} from '../../../common/assets/images';
import ChipComponent from '../../../common/components/Chip/Chip';
import CommonTable from '../../../common/components/Table/CommonTable';
import { Column } from '../../../common/components/Table/CommonTableStyles';
import {
	setPagination,
	setRowsPerPagination,
} from '../../../common/store/reducer/commonReducer';
import {
	ImgTagComponent,
	toSentenceCase,
} from '../../../common/utils/utilFunctions';

import { activitiesList, maplabelData } from './api/mock';
import GeoMap from './GeoMap';
import OverViewTabs from './OverviewTabs/OverviewTabs';
import { quickLinksList, IQuickLinkProps } from './QuickLinksData';
import UserStatusBarChart from './UserStatusBarChart';

import styles from './Overview.module.scss';

const { iconColor, textColor, headertextColor, lightTextColor } = styles;

ChartJS.register(
	Title,
	Tooltip,
	Legend,
	CategoryScale,
	ChartGeo.ChoroplethController,
	ChartGeo.ProjectionScale,
	ChartGeo.ColorScale,
	ChartGeo.GeoFeature,
);

const OverView = () => {
	const dispatch = useDispatch();
	const activityList = activitiesList;

	useEffect(() => {
		const paginationObj = {
			pageSize: 10,
			pageNumber: 0,
		};

		dispatch(setPagination(paginationObj));
		const rowsPerPageObj = {
			rowsPerPage: 10,
		};
		dispatch(setRowsPerPagination(rowsPerPageObj));
	}, []);

	const columns: readonly Column[] = [
		{
			id: 'activityTitle',
			label: 'Action',
			disablePadding: false,
			isSortable: false,
			render: (params: any) => (
				<Stack direction="column">
					<Typography className={styles['overview-action-header']}>
						{params?.activityTitle}
					</Typography>
					<Typography className={styles['overview-action-text']}>
						{params?.activityDescription}
					</Typography>
				</Stack>
			),
		},
		{
			id: 'date',
			label: 'Date/Time',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<div>
					<Stack direction="column">
						<Typography>{params?.date}</Typography>
						<Typography>{params?.time}</Typography>
					</Stack>
				</div>
			),
		},
		{
			id: 'user',
			label: toSentenceCase('Executed by'),
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Stack direction="row" spacing={1}>
					<Avatar className={styles['overview-user-avatar']}>
						<div>{params?.name.charAt(0)}</div>
					</Avatar>
					<Stack direction="column" className={styles['overview-user-column']}>
						<Typography className={styles['user-name-column']}>
							{params?.name}
						</Typography>
						<Typography className={styles['user-email-column']}>
							{params?.mail}
						</Typography>
					</Stack>
				</Stack>
			),
		},
		{
			id: 'status',
			label: 'Status',
			disablePadding: false,
			isSortable: false,
			render: (params: any) => (
				<ChipComponent customcolor={params?.status} label={params?.status} />
			),
		},
		{
			id: 'actions',
			label: 'Actions',
			disablePadding: false,
			isSortable: false,
			render: () => (
				<Stack direction="row" justifyContent="space-between">
					<IconButton sx={{ paddingTop: 0, color: iconColor }}>
						<VisibilityOutlinedIcon />
					</IconButton>
					<Typography sx={{ color: textColor }}>View</Typography>
				</Stack>
			),
		},
	];

	const onCheckBoxSelected = () => {
		// placeholder to be defined
	};
	return (
		<Grid
			container
			data-testid="overview-main-container"
			className={styles['overview-map-main-container']}
		>
			<Grid direction="row" spacing={2} container>
				<Grid
					xs={12}
					sm={12}
					md={4}
					lg={4}
					xl={4}
					item
					sx={{ maxheight: 240, minheight: 250 }}
				>
					<Card sx={{ borderRadius: 3, height: '100%' }}>
						<CardHeader
							sx={{ fontWeight: 500, fontSize: 20, color: headertextColor }}
							title={<>{toSentenceCase('User Demographics')}</>}
						/>
						<CardContent style={{ marginTop: 'auto' }}>
							<div className="test-geo">
								<Grid xs={12} item container direction="row">
									<GeoMap chosenKey="world" />
									<Grid
										container
										direction="row"
										spacing={2}
										className={styles['overview-map-labels']}
									>
										{maplabelData.map((label: any) => (
											<Grid
												item
												xs={12}
												sm={12}
												md={12}
												lg={4}
												xl={4}
												key={label?.text}
												className={styles['overview-map-labels']}
											>
												<span
													className={
														styles[`overview-map-${label.cssColor}-label`]
													}
												/>
												<Typography className={styles['overview-map-label']}>
													{label.text}
												</Typography>
											</Grid>
										))}
									</Grid>
								</Grid>
							</div>
						</CardContent>
						<CardActions
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'end',
								paddingTop: 0,
								marginTop: 0,
							}}
						>
							<IconButton aria-label="settings">
								{ImgTagComponent(rightArrowOutline, 'rightArrowOutline')}
							</IconButton>
						</CardActions>
					</Card>
				</Grid>
				<Grid xs={12} sm={12} md={4} lg={4} xl={4} item>
					<Card
						sx={{
							borderRadius: 3,
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<CardHeader
							sx={{ fontWeight: 500, fontSize: 20, color: headertextColor }}
							title={<>Active/Inactive users</>}
						/>

						<CardContent className={styles['overview-top-margin']}>
							<UserStatusBarChart />
						</CardContent>
						<CardActions
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'end',
								paddingTop: 0,
								marginTop: 0,
							}}
						>
							<IconButton aria-label="settings">
								{ImgTagComponent(rightArrowOutline, 'rightArrowOutline')}
							</IconButton>
						</CardActions>
					</Card>
				</Grid>
				<Grid xs={12} sm={12} md={4} lg={4} xl={4} item>
					<Card sx={{ borderRadius: 3, height: '100%' }}>
						<CardHeader
							sx={{ fontWeight: 500, fontSize: 20, color: headertextColor }}
							title={<>{toSentenceCase('Quick Links')}</>}
						/>
						<CardContent className={styles['overview-top-margin']}>
							<div>
								{quickLinksList.map((ele: IQuickLinkProps) => (
									<div key={ele?.value}>
										<ListItem key={ele?.value} sx={{ width: '100%' }}>
											<Stack
												direction="row"
												justifyContent="space-evenly"
												sx={{ display: 'flex', width: '100%' }}
											>
												<Stack direction="column" sx={{ width: '50%' }}>
													<ListItemText> {ele?.label}</ListItemText>
												</Stack>
												<Stack
													direction="column"
													sx={{
														display: 'flex',
														flexDirection: 'column',
														alignItems: 'end',
														width: '50%',
													}}
												>
													<Icon>
														<ArrowForwardSharpIcon />
													</Icon>
												</Stack>
											</Stack>
										</ListItem>
										<Divider />
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			<Grid container direction="row" sx={{ marginTop: 2 }}>
				<Grid xs={12} sm={12} md={6} lg={6} xl={6} item>
					<Typography
						sx={{
							fontWeight: 700,
							fontSize: 24,
							color: headertextColor,
						}}
					>
						{toSentenceCase('Recent Activities/Logs')}
					</Typography>
				</Grid>
				<Grid xs={12} sm={12} md={6} lg={6} xl={6} item textAlign="right">
					<div className={styles['overview-search-bar-filter']}>
						<ButtonComp
							btnType="secondary"
							size="medium"
							disabled
							startIcon={<CloudDownloadOutlinedIcon />}
						>
							<Typography
								sx={{ fontSize: 16, fontWeight: 500, color: lightTextColor }}
							>
								Download CSV
							</Typography>
						</ButtonComp>
					</div>
				</Grid>
			</Grid>
			<Grid container direction="row" sx={{ marginTop: 3 }}>
				<Grid xs={12} sm={12} md={6} lg={6} xl={6} item>
					<Stack className={styles['overview-search-box']}>
						<TextField
							variant="outlined"
							placeholder="search"
							InputProps={{
								startAdornment: (
									<IconButton>
										<SearchOutlined
											className={styles['overview-search-icon']}
										/>
									</IconButton>
								),
							}}
							size="small"
							className={styles['overview-searchbar-textfield']}
						/>
					</Stack>
				</Grid>
			</Grid>
			<Grid container direction="column">
				<Grid
					xs={12}
					sm={12}
					md={12}
					lg={12}
					xl={12}
					direction="row"
					container
					alignItems="baseline"
					className={styles['overview-scrollable-box']}
				>
					<Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
						<OverViewTabs />
					</Grid>
					<Grid item xs={12} sm={12} md={3} lg={4} xl={4}>
						<Box display="flex" justifyContent="flex-end">
							<Stack
								direction="row"
								justifyItems="center"
								alignItems="baseline"
							>
								<Box className={styles['overview-btn-text-transform']}>
									<span className={styles['overview-btn-text']}>
										Jan 6,2022-Jan 13,2022
									</span>
									<IconButton aria-label="settings">
										{ImgTagComponent(calendarIcon, 'calendarIcon')}
									</IconButton>
								</Box>
							</Stack>
						</Box>
					</Grid>
					<Grid item xs={12} sm={12} md={3} lg={2} xl={2}>
						<Stack direction="row" justifyContent="center">
							<ButtonComp
								btnType="text"
								startIcon={ImgTagComponent(Filter, 'Filter')}
								endIcon={ImgTagComponent(downchevronIcon, 'downchevronIcon')}
							>
								<Typography className={styles['filter-btn-style']}>
									Advanced filters
								</Typography>
							</ButtonComp>
						</Stack>
					</Grid>
				</Grid>
			</Grid>
			<Grid container direction="column">
				<CommonTable
					rows={activityList}
					columns={columns}
					defaultSortKey=""
					isCheckBoxEnabled
					onRowChecked={onCheckBoxSelected}
					tableStyles={{ minWidth: '46.875em', maxHeight: '27.5em' }}
				/>
			</Grid>
		</Grid>
	);
};
export default OverView;
